"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Clock, ArrowRight, Loader2, Terminal } from "lucide-react";
function getReadTime(content: string = ""): string {
  const wordsPerMinute = 200;
  const words = content.replace(/<[^>]*>?/gm, "").split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} MIN DI LETTURA`;
}
function ArticleItem({
  post,
  index
}: {
  post: any;
  index: number;
}) {
  const date = new Date(post.date).toLocaleDateString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const category = post.categories?.[0]?.name || "GENERALE";
  const readTime = getReadTime(post.content);
  return <div data-section-id="9378" className="group relative w-full transition-all duration-300 hover:scale-[1.01] hover:bg-white/50 hover:backdrop-blur-md" data-index={index}>
      <div className="grid grid-cols-1 gap-6 py-12 md:grid-cols-12 md:gap-8">
        {}
        <div className="flex flex-row items-center justify-between gap-4 md:col-span-3 md:flex-col md:items-start md:justify-start">
          <div className="font-mono text-xs font-semibold tracking-[0.2em] text-primary/70">
            <span className="mr-2 text-primary">|</span>
            {date}
          </div>
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
            {category}
          </div>
        </div>

        {}
        <div className="md:col-span-9">
          <Link to={post.link || "#"} className="block w-fit">
            <h3 className="article-title article-preview-title font-serif text-3xl font-semibold leading-tight tracking-tight text-foreground transition-all duration-300 group-hover:text-primary group-hover:drop-shadow-[0_2px_10px_rgba(176,155,113,0.2)] md:text-4xl">
              {post.title}
            </h3>
          </Link>
          
          <div className="mt-4 flex flex-wrap items-center gap-6 text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground/70">
            <span className="flex items-center gap-2 font-sans">
              <Clock className="h-3 w-3" />
              {readTime}
            </span>
            {post.author && <span className="hidden font-sans md:inline-block">
                
              </span>}
          </div>

          <div className="mt-6 max-w-3xl font-sans text-base leading-relaxed text-muted-foreground/80 md:text-lg" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />

          <div className="mt-8">
            <Link to={post.link || "#"}>
              <Button variant="outline" className="group/btn rounded-none border-primary/20 bg-transparent font-mono text-xs tracking-widest text-primary hover:border-primary hover:bg-primary/5">
                ESAMINA DETTAGLI
                <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover/btn:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Separator className="bg-border/10" />
    </div>;
}
function AllArticlesContent() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary/60" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary/40">
            Sincronizzazione Archivio...
          </p>
        </div>
      </div>;
  }
  if (error) {
    return <div className="py-20 text-center">
        <div className="mb-4 flex justify-center">
          <Terminal className="h-10 w-10 text-destructive/80" />
        </div>
        <p className="font-mono text-xs tracking-widest text-destructive">ERRORE: CONNESSIONE AL DATABASE INTERROTTA</p>
        <Button onClick={() => window.location.reload()} variant="outline" className="mt-4 border-destructive/30 text-destructive hover:bg-destructive/5">
          RIPRISTINA CONNESSIONE
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-20 text-center">
        <p className="font-mono text-xs tracking-widest text-muted-foreground">NESSUNA PUBBLICAZIONE TROVATA NEGLI ARCHIVI.</p>
      </div>;
  }
  return <div className="relative z-10 w-full">
      <div className="flex flex-col w-full">
        {posts.map((post, i) => <ArticleItem key={post.id} post={post} index={i} />)}
      </div>

      {hasNext && <div className="mt-16 flex justify-center">
          <Button onClick={() => loadMore()} disabled={isRefetching} variant="outline" size="lg" className="min-w-[220px] rounded-none border-primary/20 bg-background/50 font-mono text-xs tracking-[0.2em] text-primary backdrop-blur-sm hover:bg-primary/5 hover:text-primary">
            {isRefetching ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                CARICAMENTO...
              </> : "VEDI ALTRE PUBBLICAZIONI"}
          </Button>
        </div>}
    </div>;
}
export default function AllArticles() {
  const wp_query_params = {
    per_page: 6,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-news" className="relative min-h-screen w-full overflow-hidden bg-background py-24 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 z-0">
        {}
        <div className="absolute inset-0 opacity-5 mix-blend-multiply" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }}>
        </div>
        
        {}
        <div className="absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:60px_60px] opacity-20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(var(--primary)_1px,transparent_1px)] [background-size:180px_180px] opacity-10"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div className="mb-16 border-b border-border/40 pb-12">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="uppercase tracking-tighter text-foreground !font-sans !font-normal !text-6xl">NEWS E PUBBLICAZIONI</h2>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.4em] text-muted-foreground">PROSPETTIVE GIURIDICHE E ANALISI DI SETTORE</p>
            </div>
            <div className="hidden font-mono text-[10px] tracking-widest text-primary/40 md:block">
              ARCHIVIO STUDIO | ONLINE
            </div>
          </div>
        </div>

        {}
        <PostsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc"
      }}>
          <AllArticlesContent />
        </PostsProvider>
      </div>
    </section>;
}